import { inject, Injectable } from '@angular/core';
import { assert } from '@sama/js-core';
import { mapValues } from 'lodash';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import { TokenManagementService } from '../../../cross-team-shared/misc-services/token-management.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { ObjectChangeOperationService } from '../../http/persistence/draft/object-change-operation.service';
import { ShapeChangeOperationService } from '../../http/persistence/draft/shape-change-operation.service';
import { SubmitTaskService } from '../../production-workspace/submissions/submit-task.service';
import { TrainingTemplateResource } from './training-template-resource.service';

@Injectable()
export class TrainingTemplateService {
  private messagingService = inject(MessagingService);
  private tokenService = inject(TokenManagementService);
  private trainingTemplateResource = inject(TrainingTemplateResource);
  private submitAnswerService = inject(SubmitTaskService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private shapeChangeOperationService = inject(ShapeChangeOperationService);
  private objectChangeOperationService = inject(ObjectChangeOperationService);

  async updateAnswersObjectless() {
    const { answers, errors } = this.submitAnswerService.transformAnswers();
    if (errors.length) {
      throw new Error('Error while transforming answers');
    }

    // Force refresh the token before submitting, as a workaround for a bug
    // about occasional requests passing expired tokens.
    // https://bodhi.samasource.org/jira/browse/POL-578
    await this.tokenService.getToken('access_token', true);
    const { project, trainingTemplate } = this.taskWorkspaceStore.state();
    assert(
      trainingTemplate,
      'Training info must exist when templateId is present',
    );
    await this.trainingTemplateResource.updateTrainingTemplateObjectless({
      projectId: project.id,
      templateId: trainingTemplate.id,
      body: {
        name: trainingTemplate.name,
        type: trainingTemplate.type,
        passMark: trainingTemplate.passMark,
        targetTpt: trainingTemplate.targetTpt,
        answers,
      },
    });

    this.messagingService.show('Template answers updated successfully');
  }

  async updateAnswers() {
    // Force refresh the token before submitting, as a workaround for a bug
    // about occasional requests passing expired tokens.
    // https://bodhi.samasource.org/jira/browse/POL-578
    await this.tokenService.getToken('access_token', true);
    const { project, trainingTemplate } = this.taskWorkspaceStore.state();
    assert(
      trainingTemplate,
      'Training info must exist when templateId is present',
    );

    await this.trainingTemplateResource.updateTrainingTemplate({
      projectId: project.id,
      templateId: trainingTemplate.id,
      body: {
        name: trainingTemplate.name,
        type: trainingTemplate.type,
        passMark: trainingTemplate.passMark,
        targetTpt: trainingTemplate.targetTpt,
        annotationOperations: this.getAnnotationOperations(),
      },
    });

    this.messagingService.show('Template answers updated successfully');
  }

  private getAnnotationOperations() {
    const current = this.taskWorkspaceStore.state();
    const previous = {
      ...current,
      objects: {},
      outputAnnotationInfo: mapValues(
        current.outputAnnotationInfo,
        (outputInfo) => ({
          ...outputInfo!,
          shapes: {},
        }),
      ),
    };

    return [
      ...this.shapeChangeOperationService.getOperations(previous, current),
      ...this.objectChangeOperationService.getOperations(previous, current),
    ];
  }
}
